import { ANNUAL_KPIS } from '@gutterinv/shared';
import type { FinancialsResponseClient } from '../api/client.js';

export interface EvFanRow {
  ev_3_5: number | null;
  ev_4_0: number | null;
  ev_4_5: number | null;
}

/**
 * Year-end Enterprise Value at the three sensitivity multiples
 * (3.5× / 4.0× / 4.5×). The 4.0× value mirrors the worker's
 * VALUATION_MULTIPLE = 4 baseline. Returns all-null when no projection
 * is possible (no monthly net profit data → netProfitProjected is null).
 *
 * Rendered as the December endpoint of three dotted projection lines
 * on the cumulative trend chart's right-side Y-axis. The lines visually
 * emerge from the end of the solid Net line and fan out to year-end.
 */
export function yearEndEvValues(netProfitProjected: number | null): EvFanRow {
  if (netProfitProjected === null) {
    return { ev_3_5: null, ev_4_0: null, ev_4_5: null };
  }
  return {
    ev_3_5: netProfitProjected * 3.5,
    ev_4_0: netProfitProjected * 4.0,
    ev_4_5: netProfitProjected * 4.5,
  };
}

/**
 * Right Y-axis domain for the cumulative-trend EV projection lines.
 * Symmetric around the 4.0× baseline so the three year-end multiples land
 * at exact axis positions:
 *   NPP × 3.5 → 1/4 of axis (worst case)
 *   NPP × 4.0 → 1/2 of axis (base case, dead center)
 *   NPP × 4.5 → 3/4 of axis (best case)
 * Derived by solving for [a, b] in: a + 0.5(b-a) = NPP×4 ∧ a + 0.75(b-a) = NPP×4.5.
 * Result: a = NPP × 3, b = NPP × 5. Returns null when no projection is possible.
 */
export function evAxisDomain(netProfitProjected: number | null): [number, number] | null {
  if (netProfitProjected === null) return null;
  return [netProfitProjected * 3, netProfitProjected * 5];
}

/**
 * Sum a numeric key across all month rows in a financials response.
 * Null / undefined / non-numeric values are ignored.
 *
 * Returns 0 when no months have data — intentionally not null, because
 * zero YTD is a meaningful state (no months entered yet) not "missing data."
 * If a caller wants the "show — until at least one month is entered" UX,
 * it should track that separately rather than coercing this helper's
 * semantics.
 */
export function sumMonthlyKey(data: FinancialsResponseClient, key: string): number {
  let sum = 0;
  for (const monthData of Object.values(data.monthly_by_date)) {
    const v = monthData[key];
    if (typeof v === 'number') sum += v;
  }
  return sum;
}

/**
 * Strip derived KPI keys from the annual object before PUTing it back.
 *
 * The GET response injects auto-derived values (e.g. inventory_value_cents,
 * net_margin_pct) into `annual` so the UI can render them, but those keys
 * are server-owned — the Zod write schema uses .strict() and rejects them
 * on PUT. Callers MUST filter them out before sending the annual blob back,
 * or the PUT 400s with "Unrecognized key" errors and the user's edit
 * silently fails.
 *
 * Three categories of server-owned keys are stripped:
 *   1. `def.key`            when source === 'derived'
 *   2. `${def.key}_target`  when targetSource === 'derived'
 *   3. `${def.key}_projected` when source === 'derived' — added when the
 *      derive-from-monthly worker layer began injecting year-end
 *      projections into the GET response. Missed initially; caused silent
 *      failure on every annual / monthly-target edit until caught here.
 */
export function writableAnnual(
  annual: Record<string, number | null>,
): Record<string, number | null> {
  const blocked = new Set<string>();
  for (const k of ANNUAL_KPIS) {
    if (k.source === 'derived') {
      blocked.add(k.key);
      blocked.add(`${k.key}_projected`);
    }
    if (k.targetSource === 'derived') blocked.add(`${k.key}_target`);
  }
  const out: Record<string, number | null> = {};
  for (const [key, value] of Object.entries(annual)) {
    if (!blocked.has(key)) out[key] = value;
  }
  return out;
}
